/**
 * Questionnaire Translation System
 * 
 * v129.0.0: Dual-source translation.
 * 
 * PRIMARY: API-provided translations via translatedText/translatedOptions fields
 * on question objects (set when ?lang=XX is passed to GET /questionnaires).
 * 
 * FALLBACK: Local translation files (static imports) when API data doesn't
 * include translations (e.g., API unreachable, using local JSON fallback).
 * 
 * Architecture:
 * - Questions displayed in user's language
 * - Answers stored in English (for physician consistency)
 * - Falls back to English if translation not found
 */

import { useCallback } from 'react'
import { useI18n } from '../index'

// Import questionnaire translations for each language (FALLBACK source)
import qEn from './translations/en.json'
import qEs from './translations/es.json'
import qZh from './translations/zh.json'
import qVi from './translations/vi.json'
import qKo from './translations/ko.json'
import qFr from './translations/fr.json'
import qAr from './translations/ar.json'
import qRu from './translations/ru.json'
import qPt from './translations/pt.json'
import qTl from './translations/tl.json'
import qHi from './translations/hi.json'

// Map of language codes to their questionnaire translations (fallback)
const questionnaireTranslations = {
  en: qEn,
  es: qEs,
  zh: qZh,
  vi: qVi,
  ko: qKo,
  fr: qFr,
  ar: qAr,
  ru: qRu,
  pt: qPt,
  tl: qTl,
  hi: qHi,
}

/**
 * Hook for questionnaire translation functionality
 * @returns {object} - { tq (translate question), to (translate option), getEnglishValue }
 */
export function useQuestionnaireTranslation() {
  const { language } = useI18n()
  
  /**
   * Get translations for current language (with English fallback)
   */
  const getTranslations = useCallback(() => {
    return questionnaireTranslations[language] || questionnaireTranslations.en
  }, [language])
  
  /**
   * Translate question text
   * @param {string} englishText - The English question text
   * @returns {string} - Translated text or original if not found
   */
  const tq = useCallback((englishText) => {
    if (!englishText) return ''
    if (language === 'en') return englishText
    
    const translations = getTranslations()
    return translations.questions?.[englishText] || englishText
  }, [language, getTranslations])
  
  /**
   * Translate option/answer text
   * @param {string} englishText - The English option text
   * @returns {string} - Translated text or original if not found
   */
  const to = useCallback((englishText) => {
    if (!englishText) return ''
    if (language === 'en') return englishText
    
    const translations = getTranslations()
    return translations.options?.[englishText] || englishText
  }, [language, getTranslations])
  
  /**
   * Get the English value for a translated option (for storage)
   * This is important: we store answers in English for physician consistency
   * @param {string} translatedText - The translated option text shown to user
   * @returns {string} - English version for storage
   */
  const getEnglishValue = useCallback((translatedText) => {
    if (!translatedText) return ''
    if (language === 'en') return translatedText
    
    const translations = getTranslations()
    
    // Reverse lookup: find English key for this translated value
    for (const [english, translated] of Object.entries(translations.options || {})) {
      if (translated === translatedText) return english
    }
    
    // If not found in translations, assume it's already English
    return translatedText
  }, [language, getTranslations])
  
  /**
   * Translate a focus area name
   * @param {string} englishName - The English focus area name
   * @returns {string} - Translated name or original if not found
   */
  const tfa = useCallback((englishName) => {
    if (!englishName) return ''
    if (language === 'en') return englishName
    
    const translations = getTranslations()
    return translations.focusAreas?.[englishName] || englishName
  }, [language, getTranslations])
  
  return {
    tq,        // Translate question
    to,        // Translate option
    tfa,       // Translate focus area
    getEnglishValue,
    language,
  }
}

export default useQuestionnaireTranslation
